// Motor de regras do Truco Paulista para 6 jogadores (2 trios).
// Puro e determinístico (RNG com semente). Origem: TrucoEngine do protótipo.
import { GESTURES, deck, manilhaRankFor, next, nextLevel, partners, strength, team } from './cards.ts';
import { ONZE_POINTS, WIN_POINTS, normalizeHouseRules } from './house-rules.ts';
import { random, seedState, shuffle } from './rng.ts';
import type {
  Answer, Card, EndReason, Game, GameEvent, Gesture, Hand, HouseRules, Level, OnzeAnswer, Round, RoundResult, Seat, Team,
} from './types.ts';

export function newGame(seed: number, rules?: Partial<HouseRules>): Game {
  if (!Number.isInteger(seed) || seed < 0 || seed > 0xffffffff) throw new Error('semente inválida: ' + seed);
  const g: Game = {
    seed, rng: seedState(seed), rules: normalizeHouseRules(rules),
    scores: [0, 0], dealer: 0, handNo: 0, phase: 'idle', winner: null, hand: null, events: [],
  };
  g.dealer = Math.floor(random(g) * 6) as Seat;
  return g;
}

function emit<E extends GameEvent>(g: Game, ev: E): E { g.events.push(ev); return ev; }

// Mão em andamento; lança erro se não houver.
export function handOf(g: Game): Hand {
  if (!g.hand) throw new Error('nenhuma mão em andamento');
  return g.hand;
}

export function startHand(g: Game): Game {
  if (g.phase === 'game_over') throw new Error('partida encerrada');
  if (g.phase !== 'idle' && g.phase !== 'hand_over') throw new Error('mão em andamento');
  g.dealer = next(g.dealer);
  g.handNo++;
  const d = shuffle(deck(), g);
  const hands: Card[][] = [[], [], [], [], [], []];
  for (let k = 0; k < 3; k++) for (let s = 0; s < 6; s++) hands[s]!.push(d.pop()!);
  const vira = d.pop()!;
  const h: Hand = {
    hands, vira, manilha: manilhaRankFor(vira), value: 1, canRaiseTeam: null, pending: null,
    rounds: [], turn: next(g.dealer), special: null, onzeTeam: null, ended: false, result: null, dealtFirst: next(g.dealer),
  };
  g.hand = h;
  if (g.scores[0] === ONZE_POINTS && g.scores[1] === ONZE_POINTS) h.special = 'ferro';
  else if (g.scores[0] === ONZE_POINTS || g.scores[1] === ONZE_POINTS) { h.special = 'onze'; h.onzeTeam = g.scores[0] === ONZE_POINTS ? 0 : 1; }
  newRound(g, h.turn);
  g.phase = h.special === 'onze' ? 'onze' : 'play';
  emit(g, { type: 'hand_start', handNo: g.handNo, dealer: g.dealer, vira, manilha: h.manilha, special: h.special, onzeTeam: h.onzeTeam });
  return g;
}

function newRound(g: Game, leader: Seat): void {
  const h = handOf(g);
  h.rounds.push({ plays: [], leader, result: null, winnerSeat: null });
  h.turn = leader;
}
export function curRound(g: Game): Round {
  const h = handOf(g);
  return h.rounds[h.rounds.length - 1]!;
}

// ---- consultas para a interface e para os bots ----
export function canPlay(g: Game, seat: Seat): boolean { return g.phase === 'play' && g.hand!.turn === seat; }
// Esconder carta vale em toda sala. Na 1ª rodada, ninguém esconde, nem o mão. Na 2ª e na 3ª, todos podem esconder,
// inclusive quem abre a rodada, menos na rodada que vem depois de uma rodada empachada: ela é jogada com todas as
// cartas abertas. Vale em todas as mãos, inclusive onze e ferro. A tela usa a mesma conta (rounds da visão).
export function podeEsconderNaRodada(rounds: readonly { readonly result: RoundResult | null }[]): boolean {
  const n = rounds.length;
  return n > 1 && rounds[n - 2]!.result !== 'tie';
}
export function canHide(g: Game, seat: Seat): boolean {
  return canPlay(g, seat) && podeEsconderNaRodada(g.hand!.rounds);
}
export function canTruco(g: Game, seat: Seat): boolean {
  if (!canPlay(g, seat)) return false;
  const h = g.hand!;
  if (h.special || h.pending) return false;
  if (nextLevel(h.value) === null) return false;
  return h.canRaiseTeam === null || h.canRaiseTeam === team(seat);
}
// Sinais valem durante qualquer mão, inclusive mão de onze e mão de ferro.
export function canSignal(g: Game, seat: Seat): boolean {
  const h = g.hand;
  return g.rules.sinais !== 'desligados' && !!h && !h.ended && (g.phase === 'play' || g.phase === 'respond' || g.phase === 'onze') && seat >= 0 && seat < 6;
}
export function trucoLevel(g: Game): Level | null { return nextLevel(handOf(g).value); }
export function respondingTeam(g: Game): Team | null { return g.phase === 'respond' ? (1 - g.hand!.pending!.byTeam) as Team : null; }

// ---- ações ----
export function play(g: Game, seat: Seat, card: Card, hidden?: boolean): Game {
  if (!canPlay(g, seat)) throw new Error('não é a vez de ' + seat);
  const h = g.hand!;
  const idx = h.hands[seat]!.indexOf(card);
  if (idx < 0) throw new Error('carta fora da mão: ' + card);
  if (hidden && !canHide(g, seat)) throw new Error('não pode esconder agora');
  h.hands[seat]!.splice(idx, 1);
  const rd = curRound(g);
  rd.plays.push({ seat, card, hidden: !!hidden });
  emit(g, { type: 'play', seat, card, hidden: !!hidden, round: h.rounds.length - 1 });
  if (rd.plays.length === 6) resolveRound(g);
  else h.turn = next(h.turn);
  return g;
}

// Tempo da vez esgotado: joga a última carta da mão de quem está na vez, aberta.
export function timeoutPlay(g: Game): Game {
  if (g.phase !== 'play') throw new Error('ninguém está na vez');
  const h = g.hand!, cards = h.hands[h.turn]!;
  return play(g, h.turn, cards[cards.length - 1]!, false);
}

function resolveRound(g: Game): void {
  const h = g.hand!, rd = curRound(g);
  let best = -1, holders: Seat[] = [];
  rd.plays.forEach((p) => {
    if (p.hidden) return;
    const s = strength(p.card, h.manilha);
    if (s > best) { best = s; holders = [p.seat]; } else if (s === best) holders.push(p.seat);
  });
  const teams = holders.map(team).filter((t, i, a) => a.indexOf(t) === i);
  // As seis cartas cobertas (quem abre também pode esconder): nenhuma vale e a rodada empacha.
  rd.winnerSeat = holders[0] ?? rd.leader;
  rd.result = teams.length === 1 ? teams[0]! : 'tie';
  emit(g, { type: 'round_end', round: h.rounds.length - 1, result: rd.result, winnerSeat: rd.winnerSeat });
  const w = decide(h.rounds.map((x) => x.result!));
  if (w !== undefined) endHand(g, w, 'rounds');
  // Empachou: quem abre a próxima rodada é o mão (o jogador depois de quem deu as cartas), não quem empachou.
  else newRound(g, rd.result === 'tie' ? next(g.dealer) : rd.winnerSeat);
}

// undefined = continua; null = ninguém pontua; 0/1 = trio vencedor
export function decide(r: RoundResult[]): Team | null | undefined {
  const w0 = r.filter((x) => x === 0).length, w1 = r.filter((x) => x === 1).length;
  if (w0 === 2) return 0; if (w1 === 2) return 1;
  if (r.length === 2) {
    if (r[0] === 'tie' && r[1] !== 'tie') return r[1];
    if (r[0] !== 'tie' && r[1] === 'tie') return r[0];
    return undefined;
  }
  if (r.length === 3) {
    if (r[2] !== 'tie') return r[2];
    if (r[0] !== 'tie') return r[0];
    if (r[1] !== 'tie') return r[1];
    return null;
  }
  return undefined;
}

export function requestTruco(g: Game, seat: Seat): Game {
  if (!canTruco(g, seat)) throw new Error('truco não permitido');
  const h = g.hand!, lvl = nextLevel(h.value)!;
  h.pending = { level: lvl, byTeam: team(seat), bySeat: seat };
  g.phase = 'respond';
  emit(g, { type: 'truco', seat, level: lvl });
  return g;
}

export function respond(g: Game, seat: Seat, answer: Answer): Game {
  if (g.phase !== 'respond') throw new Error('sem pedido pendente');
  const h = g.hand!, p = h.pending!;
  if (team(seat) !== 1 - p.byTeam) throw new Error('este trio não responde');
  if (answer === 'accept') {
    h.value = p.level; h.canRaiseTeam = team(seat); h.pending = null; g.phase = 'play';
    emit(g, { type: 'accept', seat, level: h.value });
  } else if (answer === 'run') {
    emit(g, { type: 'run', seat, level: p.level, points: h.value });
    h.pending = null;
    endHand(g, p.byTeam, 'run');
  } else if (answer === 'raise') {
    const nl = nextLevel(p.level);
    if (nl === null) throw new Error('não há aumento depois do doze');
    h.value = p.level;
    h.pending = { level: nl, byTeam: team(seat), bySeat: seat };
    emit(g, { type: 'raise', seat, level: nl, accepted: p.level });
  } else throw new Error('resposta inválida');
  return g;
}

export function onzeDecision(g: Game, answer: OnzeAnswer): Game {
  if (g.phase !== 'onze') throw new Error('não é mão de onze');
  const h = g.hand!, t = h.onzeTeam!;
  if (answer === 'play') { h.value = 3; g.phase = 'play'; emit(g, { type: 'onze_play', team: t }); }
  else if (answer === 'run') { h.value = 1; emit(g, { type: 'onze_run', team: t }); endHand(g, (1 - t) as Team, 'onze_run'); }
  else throw new Error('resposta inválida');
  return g;
}

function endHand(g: Game, winnerTeam: Team | null, reason: EndReason): void {
  const h = g.hand!;
  h.ended = true;
  const pts = winnerTeam === null ? 0 : h.value;
  if (winnerTeam !== null) g.scores[winnerTeam] = Math.min(WIN_POINTS, g.scores[winnerTeam] + pts);
  h.result = { team: winnerTeam, points: pts, reason };
  g.phase = 'hand_over';
  emit(g, { type: 'hand_end', team: winnerTeam, points: pts, reason, scores: g.scores.slice() });
  if (g.scores[0] >= WIN_POINTS || g.scores[1] >= WIN_POINTS) {
    g.winner = g.scores[0] >= WIN_POINTS ? 0 : 1; g.phase = 'game_over';
    emit(g, { type: 'game_over', team: g.winner, scores: g.scores.slice() });
  }
}

// Sinal: vai sempre para os dois parceiros (um toque no gesto envia). O evento guarda os dois destinos, que o trio vê.
export function signal(g: Game, seat: Seat, gesture: Gesture): GameEvent & { type: 'signal' } {
  if (!canSignal(g, seat)) throw new Error('sinal não permitido agora');
  if (!GESTURES.includes(gesture)) throw new Error('gesto inválido: ' + gesture);
  return emit(g, { type: 'signal', seat, gesture, to: partners(seat) });
}
